/* ==========================================================================
   DSS: desktop hover magnifier for the PDP gallery.

   Roll the mouse over the main product image and it zooms in place, panning
   with the cursor. A higher-resolution copy of the same Shopify CDN image is
   used for the zoom layer, so it is genuinely sharper, not just scaled up.

   Behaviour decisions
   - Mouse/fine-pointer only (`(hover: hover) and (pointer: fine)`). Touch and
     coarse pointers never run any of this, so phones/tablets keep exactly the
     theme behaviour: tap -> PhotoSwipe lightbox.
   - CLICK IS LEFT ALONE. Impulse renders a transparent, full-cover
     `.product__photo-zoom` button over the main image on desktop, so clicking
     anywhere on the image opens the lightbox. Hover-zoom + click-for-full-view
     coexist: our layer is `pointer-events:none`, so every click still reaches
     that button.
   - Everything is delegated off `document`, and the frame under the cursor is
     resolved on every mousemove, so thumbnail navigation, Flickity slide
     changes and variant image switches need no re-binding.
   - The quick view modal (`.modal--quick-shop`) is deliberately excluded.
   ========================================================================== */

(function () {
  'use strict';

  if (!window.matchMedia || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    return;
  }

  var ZOOM = 2.4; // magnification of the displayed frame
  /* Hover INTENT: Sam found instant activation jarring when the cursor
     merely crosses the image en route to the buy box (a classic hover-zoom
     failure). The zoom now engages only after the cursor DWELLS on the
     frame; brushing across does nothing. Paired with a fade-in in the CSS. */
  var ENTER_DELAY = 220; // ms of dwell before the zoom engages
  var intentTimer = null;
  var pendingWrap = null;
  var MIN_FRAME = 260; // px - don't bother on very small frames
  var WIDTH_STEPS = [720, 900, 1080, 1200, 1440, 1600, 1800];
  var FRAME_SELECTOR = '.product__photos .product-main-slide .image-wrap';

  var active = null; // { wrap, img, layer }
  var lastX = 0;
  var lastY = 0;
  var suppressed = false; // true while a mouse button is down (Flickity drag)
  var ticking = false;
  var hasPoint = false;
  var refreshQueued = false;
  var lastSelected = null;
  var settleTimer = null;

  function lightboxOpen() {
    var pswp = document.querySelector('.pswp');
    return !!(pswp && pswp.classList.contains('pswp--open'));
  }

  function frameFor(target) {
    if (!target || typeof target.closest !== 'function') return null;
    var wrap = target.closest(FRAME_SELECTOR);
    if (!wrap || wrap.closest('.modal--quick-shop')) return null;
    var img = wrap.querySelector('img.photoswipe__image');
    if (!img) return null; // video / 3d model slides
    if (!wrap.offsetParent) return null; // hidden (e.g. video slide on desktop)
    var rect = wrap.getBoundingClientRect();
    if (rect.width < MIN_FRAME) return null;
    return { wrap: wrap, img: img };
  }

  // Build the highest-resolution URL worth requesting for this frame:
  // the frame's own CDN image with a bigger `width` param, capped by the
  // media's native width so we never ask Shopify for pixels that don't exist.
  function buildUrl(img, wrap) {
    var base = img.getAttribute('data-photoswipe-src') || img.currentSrc || img.src;
    if (!base) return null;
    if (base.indexOf('//') === 0) base = window.location.protocol + base;

    var frameWidth = wrap.getBoundingClientRect().width;
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var needed = frameWidth * ZOOM * dpr;

    var target = WIDTH_STEPS[WIDTH_STEPS.length - 1];
    for (var i = 0; i < WIDTH_STEPS.length; i++) {
      if (WIDTH_STEPS[i] >= needed) { target = WIDTH_STEPS[i]; break; }
    }

    var native = parseInt(img.getAttribute('data-photoswipe-width'), 10);
    if (native > 0) target = Math.max(Math.min(target, native), Math.ceil(frameWidth));

    try {
      var url = new URL(base, window.location.href);
      if (url.searchParams.has('width') || /\/cdn\//.test(url.pathname)) {
        url.searchParams.set('width', String(target));
      }
      return url.href;
    } catch (e) {
      return base;
    }
  }

  function position() {
    ticking = false;
    if (!active) return;
    var rect = active.wrap.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    var px = (lastX - rect.left) / rect.width;
    var py = (lastY - rect.top) / rect.height;
    px = px < 0 ? 0 : px > 1 ? 1 : px;
    py = py < 0 ? 0 : py > 1 ? 1 : py;
    active.layer.style.backgroundPosition = (px * 100).toFixed(2) + '% ' + (py * 100).toFixed(2) + '%';
  }

  function schedule() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(position);
  }

  function sizeLayer() {
    if (!active) return;
    var rect = active.wrap.getBoundingClientRect();
    var ratio = parseFloat(active.layer.dataset.ratio) || 1; // natural w / h
    var w = rect.width * ZOOM;
    var h = w / ratio;
    if (h < rect.height) { h = rect.height; w = h * ratio; }
    active.layer.style.backgroundSize = Math.round(w) + 'px ' + Math.round(h) + 'px';
  }

  function show() {
    if (!active || suppressed) return;
    sizeLayer();
    position();
    active.wrap.classList.add('dss-zoom-active');
  }

  function deactivate() {
    if (!active) return;
    active.wrap.classList.remove('dss-zoom-active');
    active = null;
  }

  function activate(frame) {
    if (lightboxOpen()) return;

    var layer = frame.wrap.querySelector('.dss-zoom-layer');
    if (!layer) {
      layer = document.createElement('div');
      layer.className = 'dss-zoom-layer';
      layer.setAttribute('aria-hidden', 'true');
      frame.wrap.appendChild(layer);
      frame.wrap.classList.add('dss-zoom-frame');
    }
    active = { wrap: frame.wrap, img: frame.img, layer: layer };

    if (layer.dataset.loaded === '1') { show(); return; }
    if (layer.dataset.loading === '1') return;

    var url = buildUrl(frame.img, frame.wrap);
    if (!url) { deactivate(); return; }

    layer.dataset.loading = '1';
    var pre = new Image();
    pre.onload = function () {
      layer.dataset.loading = '';
      layer.dataset.loaded = '1';
      layer.dataset.ratio = pre.naturalHeight ? (pre.naturalWidth / pre.naturalHeight) : 1;
      layer.style.backgroundImage = 'url("' + url + '")';
      if (active && active.layer === layer) show();
    };
    pre.onerror = function () {
      layer.dataset.loading = '';
      layer.dataset.failed = '1';
    };
    pre.src = url;
  }

  function point(evt) {
    lastX = evt.clientX;
    lastY = evt.clientY;
    hasPoint = true;
  }

  // The slide under a stationary cursor can change without any mouse event
  // (variant image switch, thumbnail click, Flickity settle). Re-resolve from
  // the last known cursor position so the zoom follows the new image.
  function refresh() {
    if (!hasPoint || suppressed || lightboxOpen()) { deactivate(); return; }
    var el = document.elementFromPoint(lastX, lastY);
    var frame = frameFor(el);
    if (!frame) { deactivate(); return; }
    if (!active || active.wrap !== frame.wrap) {
      deactivate();
      activate(frame);
    } else {
      sizeLayer();
      schedule();
    }
  }

  function clearIntent() {
    if (intentTimer) { clearTimeout(intentTimer); intentTimer = null; }
    pendingWrap = null;
  }

  function handle(evt) {
    point(evt);
    if (suppressed || lightboxOpen()) { clearIntent(); deactivate(); return; }
    var frame = frameFor(evt.target);
    if (!frame) { clearIntent(); deactivate(); return; }
    if (active && active.wrap === frame.wrap) { schedule(); return; }
    /* new frame under the cursor: require dwell before engaging */
    if (pendingWrap !== frame.wrap) {
      clearIntent();
      pendingWrap = frame.wrap;
      intentTimer = setTimeout(function () {
        intentTimer = null;
        var el = document.elementFromPoint(lastX, lastY);
        var f = frameFor(el);
        if (!f || f.wrap !== pendingWrap) { pendingWrap = null; return; }
        pendingWrap = null;
        if (suppressed || lightboxOpen()) return;
        deactivate();
        activate(f);
      }, ENTER_DELAY);
    }
  }

  document.addEventListener('mouseover', handle, { passive: true });
  document.addEventListener('mousemove', handle, { passive: true });

  document.addEventListener('mouseout', function (evt) {
    if (evt.relatedTarget && !frameFor(evt.relatedTarget)) clearIntent();
    if (!active) return;
    var to = evt.relatedTarget;
    if (!to || !active.wrap.contains(to)) deactivate();
  }, { passive: true });

  // Flickity drag: hide while the button is down, restore on release.
  document.addEventListener('mousedown', function () {
    suppressed = true;
    deactivate();
  }, { passive: true });

  document.addEventListener('mouseup', function () {
    suppressed = false;
    // A click on the image opens the PhotoSwipe lightbox; drop the zoom layer
    // once it is up so nothing lingers behind (or during) the overlay.
    window.setTimeout(function () { if (lightboxOpen()) deactivate(); }, 60);
  }, { passive: true });

  // Watch the gallery for slide changes (Flickity toggles .is-selected) so a
  // variant image switch or thumbnail click updates the zoom immediately, even
  // if the cursor never moves.
  function watchGallery() {
    var galleries = document.querySelectorAll('.product__photos [data-product-photos]');
    if (!galleries.length || !window.MutationObserver) return;
    var observer = new MutationObserver(function () {
      // Only react to an actual slide change — our own class toggles land in
      // this same subtree and must not feed back into the observer.
      var selected = document.querySelector('.product__photos .product-main-slide.is-selected');
      if (selected === lastSelected) return;
      lastSelected = selected;
      if (refreshQueued) return;
      refreshQueued = true;
      window.requestAnimationFrame(function () {
        refreshQueued = false;
        if (hasPoint) refresh();
        // ...and once more after Flickity has settled the transition.
        window.clearTimeout(settleTimer);
        settleTimer = window.setTimeout(function () { if (hasPoint) refresh(); }, 420);
      });
    });
    galleries.forEach(function (gallery) {
      if (gallery.closest('.modal--quick-shop')) return;
      observer.observe(gallery, { subtree: true, attributes: true, attributeFilter: ['class'] });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchGallery);
  } else {
    watchGallery();
  }

  // Keep the zoomed region under the cursor when the page scrolls or resizes
  // without the mouse moving.
  window.addEventListener('scroll', function () { if (active) schedule(); }, { passive: true });
  window.addEventListener('resize', function () {
    if (!active) return;
    sizeLayer();
    schedule();
  }, { passive: true });
})();
